/**
 * Encrypted on-device storage for the offline outbox.
 *
 * Only records waiting to be sent are stored (new registrations and walk-ins captured while the
 * server is unreachable). They are encrypted with AES-GCM using a non-extractable key kept in
 * IndexedDB, and deleted as soon as the server confirms them. This keeps raw patient details out
 * of plain browser storage; it does not replace OS-level disk encryption and screen locking.
 */

const DB_NAME = 'lumahms-offline'
const DB_VERSION = 1
const OUTBOX = 'outbox'
const KEYS = 'keys'

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const r = indexedDB.open(DB_NAME, DB_VERSION)
      r.onupgradeneeded = () => {
        const db = r.result
        if (!db.objectStoreNames.contains(OUTBOX)) db.createObjectStore(OUTBOX, { keyPath: 'id' })
        if (!db.objectStoreNames.contains(KEYS)) db.createObjectStore(KEYS)
      }
      r.onsuccess = () => resolve(r.result)
      r.onerror = () => reject(r.error)
    })
  }
  return dbPromise
}

async function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open()
  return req(fn(db.transaction(store, mode).objectStore(store)))
}

/** Test hook: close and forget the cached connection so the database can be deleted. */
export async function _resetForTests() {
  if (dbPromise) (await dbPromise).close()
  dbPromise = null
  keyPromise = null
}

let keyPromise: Promise<CryptoKey> | null = null

function deviceKey(): Promise<CryptoKey> {
  if (!keyPromise) {
    keyPromise = (async () => {
      const existing = await tx<CryptoKey | undefined>(KEYS, 'readonly', (s) => s.get('outbox') as IDBRequest<CryptoKey | undefined>)
      if (existing) return existing
      const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
      await tx(KEYS, 'readwrite', (s) => s.put(key, 'outbox'))
      return key
    })()
  }
  return keyPromise
}

export interface Sealed {
  iv: Uint8Array<ArrayBuffer>
  data: ArrayBuffer
}

export async function seal(value: unknown): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await deviceKey(), new TextEncoder().encode(JSON.stringify(value)))
  return { iv, data }
}

export async function unseal<T>(s: Sealed): Promise<T> {
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: s.iv }, await deviceKey(), s.data)
  return JSON.parse(new TextDecoder().decode(plain)) as T
}

export const outboxStore = {
  put: <T extends { id: string }>(row: T) => tx(OUTBOX, 'readwrite', (s) => s.put(row)),
  get: <T,>(id: string) => tx<T | undefined>(OUTBOX, 'readonly', (s) => s.get(id) as IDBRequest<T | undefined>),
  all: <T,>() => tx<T[]>(OUTBOX, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),
  remove: (id: string) => tx(OUTBOX, 'readwrite', (s) => s.delete(id)),
}
